/**
 * CompanyNamePage — iter-218r (Feb 2026)
 *
 * Dedicated page for editing the company's AR + EN name that is auto-injected
 * into every certificate signature block. Previously this lived as an
 * unlabelled section INSIDE the "company" tab of CertificateSubjectsControl
 * which admins couldn't find. Now it sits as its own screen under
 * "ختم ومسمى الشركة".
 *
 * Backend endpoints (unchanged):
 *   GET  /api/admin/certificates/company-name
 *   PUT  /api/admin/certificates/company-name
 * Translation:
 *   POST /api/utils/translate   { text, source_lang, target_lang }
 *
 * Route: /control-panel-settings/company-mgmt/branding/name
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Building2, Loader2, Save, Languages } from 'lucide-react';
import { toast } from 'sonner';

import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function CompanyNamePage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [nameAr, setNameAr] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [meta,   setMeta]   = useState({ updated_at: null, updated_by: null });
  const [loading,   setLoading]   = useState(true);
  const [saving,    setSaving]    = useState(false);
  const [dirty,     setDirty]     = useState(false);
  const [translating, setTranslating] = useState({ ar2en: false, en2ar: false });

  // ── Load ───────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/admin/certificates/company-name`,
          { headers: getAuthHeaders() },
        );
        setNameAr(r.data?.name_ar || '');
        setNameEn(r.data?.name_en || '');
        setMeta({ updated_at: r.data?.updated_at || null, updated_by: r.data?.updated_by || null });
      } catch (e) {
        toast.error(t('فشل تحميل اسم الشركة', 'Failed to load company name'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Translate helper (uses the same /api/utils/translate endpoint the
  //     certificate subjects page uses so we stay consistent) ────────
  const translate = async (direction) => {
    const src = direction === 'ar2en' ? nameAr.trim() : nameEn.trim();
    if (!src) {
      toast.error(t('اكتب الاسم أولاً', 'Enter the name first'));
      return;
    }
    setTranslating((s) => ({ ...s, [direction]: true }));
    try {
      const r = await axios.post(
        `${BACKEND_URL}/api/utils/translate`,
        {
          text: src,
          source_lang: direction === 'ar2en' ? 'ar' : 'en',
          target_lang: direction === 'ar2en' ? 'en' : 'ar',
        },
        { headers: getAuthHeaders() },
      );
      const out = r.data?.translated || '';
      if (!out) throw new Error('empty');
      if (direction === 'ar2en') setNameEn(out); else setNameAr(out);
      setDirty(true);
      toast.success(t('تمت الترجمة', 'Translated'));
    } catch (e) {
      toast.error(t('فشلت الترجمة', 'Translation failed'));
    } finally {
      setTranslating((s) => ({ ...s, [direction]: false }));
    }
  };

  // ── Save ───────────────────────────────────────────────────────────
  const save = async () => {
    if (!nameAr.trim() || !nameEn.trim()) {
      toast.error(t('املأ الاسم بالعربي والإنجليزي', 'Fill both Arabic and English names'));
      return;
    }
    setSaving(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/certificates/company-name`,
        { name_ar: nameAr.trim(), name_en: nameEn.trim() },
        { headers: getAuthHeaders() },
      );
      toast.success(t('✓ حُفظ اسم الشركة', '✓ Company name saved'));
      setDirty(false);
      // Refresh timestamp
      try {
        const r = await axios.get(
          `${BACKEND_URL}/api/admin/certificates/company-name`,
          { headers: getAuthHeaders() },
        );
        setMeta({ updated_at: r.data?.updated_at || null, updated_by: r.data?.updated_by || null });
      } catch { /* non-fatal */ }
    } catch (e) {
      toast.error(t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout
      title="مسمى الشركة"
      titleEn="Company Name"
      subtitle="الاسم بالعربي والإنجليزي — يظهر في كل الشهادات الرسمية"
      subtitleEn="Arabic + English name — displayed on every official certificate"
      screenCode="FZLC-CO-NAME"
      backPath="/control-panel-settings/company-mgmt/branding"
      onBack={() => navigate('/control-panel-settings/company-mgmt/branding')}
      breadcrumb={[
        { labelAr: 'لوحة التحكم', labelEn: 'Dashboard', path: '/dashboard' },
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: '/control-panel-settings' },
        { labelAr: 'التحكم بإدارة الشركة', labelEn: 'Company Management Control', path: '/control-panel-settings/company-mgmt' },
        { labelAr: 'ختم ومسمى الشركة', labelEn: 'Company Stamp & Name', path: '/control-panel-settings/company-mgmt/branding' },
        { labelAr: 'مسمى الشركة', labelEn: 'Company Name' },
      ]}
    >
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="company-name-page">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl bg-white/[0.02] border border-white/10 p-6"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 rounded-xl bg-[#3B9EDC]/15 border border-[#3B9EDC]/40 flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#3B9EDC]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">
                {t('الاسم الرسمي للشركة', 'Official Company Name')}
              </h2>
              <p className="text-xs text-white/50 mt-0.5">
                {t('يُحقن تلقائياً في كل شهادة "لمن يهمه الأمر" أو "تهنئة"',
                   'Auto-injected into every "To-Whom-It-May-Concern" & Congratulations certificate')}
              </p>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-white/60">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              {t('جاري التحميل…', 'Loading…')}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 gap-4">
                {/* Arabic */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[12px] font-medium text-white/70">
                      {t('بالعربي', 'Arabic')}
                    </label>
                    <button
                      type="button"
                      onClick={() => translate('en2ar')}
                      disabled={translating.en2ar || !nameEn.trim()}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 hover:bg-amber-500/20 disabled:opacity-40"
                      data-testid="company-name-translate-en2ar"
                      title={t('ترجم من الإنجليزية إلى العربية', 'Translate EN → AR')}
                    >
                      {translating.en2ar
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Languages className="w-3 h-3" /> EN → AR</>}
                    </button>
                  </div>
                  <Input
                    value={nameAr}
                    onChange={(e) => { setNameAr(e.target.value); setDirty(true); }}
                    dir="rtl"
                    className="bg-[#0a0a0a] border-white/10 text-white h-12 text-base"
                    placeholder={t('مثال: شركة أ إس بي 606 للاستشارات', 'e.g. ASB606 Consultancy LLC')}
                    data-testid="company-name-ar-input"
                  />
                </div>
                {/* English */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-[12px] font-medium text-white/70">
                      {t('بالإنجليزية', 'English')}
                    </label>
                    <button
                      type="button"
                      onClick={() => translate('ar2en')}
                      disabled={translating.ar2en || !nameAr.trim()}
                      className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-blue-500/10 border border-blue-500/30 text-blue-200 hover:bg-blue-500/20 disabled:opacity-40"
                      data-testid="company-name-translate-ar2en"
                      title={t('ترجم من العربية إلى الإنجليزية', 'Translate AR → EN')}
                    >
                      {translating.ar2en
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <><Languages className="w-3 h-3" /> AR → EN</>}
                    </button>
                  </div>
                  <Input
                    value={nameEn}
                    onChange={(e) => { setNameEn(e.target.value); setDirty(true); }}
                    dir="ltr"
                    className="bg-[#0a0a0a] border-white/10 text-white h-12 text-base"
                    placeholder="e.g. ASB606 Consultancy LLC"
                    data-testid="company-name-en-input"
                  />
                </div>
              </div>

              {meta.updated_at && (
                <p className="mt-4 text-[11px] text-white/40">
                  {t(`آخر تحديث: ${new Date(meta.updated_at).toLocaleString('ar-AE')} — بواسطة: ${meta.updated_by || '—'}`,
                     `Last updated: ${new Date(meta.updated_at).toLocaleString('en-GB')} — by ${meta.updated_by || '—'}`)}
                </p>
              )}

              <div className="mt-6 flex items-center justify-end">
                <Button
                  onClick={save}
                  disabled={!dirty || saving || !nameAr.trim() || !nameEn.trim()}
                  className="bg-[#3B9EDC] hover:bg-[#0369a1] text-white h-11 px-6"
                  data-testid="company-name-save-btn"
                >
                  {saving
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <><Save className="w-4 h-4 me-2" /> {t('حفظ', 'Save')}</>}
                </Button>
              </div>
            </>
          )}
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
